"use client";
import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Modal,
  Table,
} from "react-bootstrap";
import { FaPlus, FaEye, FaRegEdit, FaTrash } from "react-icons/fa";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { uploadToCloudinary } from "../cloudinaryConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

export default function CategoriesItemsAdmin() {
  const [show, setShow] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);


  const validationSchema = Yup.object({
    name: Yup.string().required("Category name is required"),
    image: Yup.mixed().required("Category image is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      image: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!imageFile && !existingImageUrl) {
        toast.error("Please select an image for the category.", {
          position: "top-right",
          autoClose: 1000,
        });
        return;
      }

      setUploading(true);
      try {
        let imageUrl = existingImageUrl;

        if (imageFile) {
          imageUrl = await uploadToCloudinary(imageFile);
        }

        const categoryData = {
          name: values.name,
          image: imageUrl,
        };

        if (isEdit) {
          const categoryDoc = doc(db, "categories", editId);
          await updateDoc(categoryDoc, categoryData);
          toast.success("Category updated successfully!", {
            position: "top-right",
            autoClose: 1000,
          });
        } else {
          await addDoc(collection(db, "categories"), categoryData);
          toast.success("Category added successfully!", {
            position: "top-right",
            autoClose: 1000,
          });
        }

        formik.resetForm();
        removeImage();
        handleClose();
      } catch (error) {
        console.error("Error adding/updating category: ", error);
        toast.error(`Error: ${error.message || 'Failed to save category'}`, {
          position: "top-right",
          autoClose: 2000,
        });
      } finally {
        setUploading(false);
      }
    },
  });

  const handleClose = () => {
    setShow(false);
    setIsEdit(false);
    formik.resetForm();
    removeImage();
  };
  const handleShow = () => setShow(true);

  const setImage = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith("image/")) {
        toast.error("Please select a valid image file.", {
          position: "top-right",
          autoClose: 2000,
        });
        return;
      }

      if (selectedFile.type === 'image/heic' || selectedFile.type === 'image/heif') {
        toast.error("HEIC/HEIF format not supported. Please convert to JPG/PNG.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageFile(selectedFile);
        setExistingImageUrl(null);
        formik.setFieldValue("image", selectedFile);
        toast.success(`Image selected! (${(selectedFile.size / 1024).toFixed(0)} KB)`, {
          position: "top-right",
          autoClose: 1500,
        });
      };
      reader.onerror = () => {
        toast.error("Failed to read image file.", {
          position: "top-right",
          autoClose: 2000,
        });
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    formik.setFieldValue("image", null);
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "categories"), (snapshot) => {
      const categoriesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    });

    return () => unsubscribe();
  }, []);

  const handleEdit = (id) => {
    const categoryToEdit = categories.find((item) => item.id === id);
    formik.setValues({
      name: categoryToEdit.name,
      image: categoryToEdit.image,
    });
    setExistingImageUrl(categoryToEdit.image);
    setImagePreview(null);
    setImageFile(null);
    setIsEdit(true);
    setEditId(id);
    setShow(true);
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: "Confirm to Delete",
      message: "Are you sure you want to delete this category?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              await deleteDoc(doc(db, "categories", id));
              setCategories(categories.filter((item) => item.id !== id));
              toast.success("Category deleted successfully!", {
                position: "top-right",
                autoClose: 1000,
              });
            } catch (error) {
              console.error("Error deleting category:", error);
              toast.error("Failed to delete category.", {
                position: "top-right",
                autoClose: 1000,
              });
            }
          },
        },
        {
          label: "No",
          onClick: () => { },
        },
      ],
    });
  };

  const handleView = (id) => {
    const categoryToView = categories.find((item) => item.id === id);
    setSelectedCategory(categoryToView);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
  };

  return (
    <Container fluid className="py-4">
      <ToastContainer />
      <Card className="admin-card border-0 mb-4">
        <div className="admin-card-header">
          <h4 className="admin-card-title">Categories Management</h4>
          <Button style={{ backgroundColor: "var(--admin-primary)", borderColor: "var(--admin-primary)" }} onClick={handleShow} className="d-flex align-items-center gap-2 shadow-sm">
            <FaPlus /> Add New Category
          </Button>
        </div>
      </Card>

      <Card className="admin-card border-0">
        <Card.Body className="p-0">
          <div className="admin-table-container">
            <Table responsive className="admin-table mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="px-4 py-3">Category Name</th>
                  <th className="px-4 py-3">Category Image</th>
                  <th className="px-4 py-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length > 0 ? (
                  categories.map((cat) => (
                    <tr key={cat.id}>
                      <td className="px-4 fw-medium">{cat.name}</td>
                      <td className="px-4">
                        <img src={cat.image} alt={cat.name} style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "8px" }} />
                      </td>
                      <td className="px-4 text-end">
                        <div className="admin-action-btns">
                          <button type="button" className="btn-action btn-action-view" onClick={() => handleView(cat.id)} title="View">
                            <FaEye size={18} />
                          </button>
                          <button type="button" className="btn-action btn-action-edit" onClick={() => handleEdit(cat.id)} title="Edit">
                            <FaRegEdit size={18} />
                          </button>
                          <button type="button" className="btn-action btn-action-delete" onClick={() => handleDelete(cat.id)} title="Delete">
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-4 text-muted">No categories found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal show={show} onHide={handleClose} centered backdrop="static" fullscreen="md-down">
        <Modal.Header closeButton className="border-bottom px-3 px-md-4 py-3 bg-light">
          <Modal.Title className="fw-bold m-0" style={{ fontSize: "1.25rem", color: "var(--admin-text-main)" }}>
            {isEdit ? "Edit Category" : "Add New Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 p-md-4">
          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="form-label">Category Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter category name"
                {...formik.getFieldProps("name")}
                isInvalid={!!(formik.touched.name && formik.errors.name)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="form-label">Category Image</Form.Label>
              <div>
                <Button variant="outline-secondary" className="w-100 position-relative" disabled={uploading}>
                  {imagePreview || existingImageUrl ? "Change Image" : "Upload Image"}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={setImage}
                    className="position-absolute top-0 start-0 w-100 h-100 opacity-0"
                    style={{ cursor: "pointer" }}
                  />
                </Button>
              </div>
              {formik.touched.image && formik.errors.image && (
                <div className="text-danger small mt-1">{formik.errors.image}</div>
              )}
            </Form.Group>

            {existingImageUrl && !imagePreview && (
              <div className="mb-3">
                <Form.Label className="form-label text-muted">Current Image</Form.Label>
                <div className="bg-light rounded p-2 mb-2 d-flex justify-content-center" style={{ border: "1px dashed var(--admin-border)" }}>
                  <img src={existingImageUrl} alt="Existing" style={{ maxHeight: "180px", maxWidth: "100%", objectFit: "contain" }} />
                </div>
                <Button variant="outline-danger" size="sm" onClick={removeImage}>Remove Image</Button>
              </div>
            )}

            {imagePreview && (
              <div className="mb-3">
                <Form.Label className="form-label text-muted">New Image Preview</Form.Label>
                <div className="bg-light rounded p-2 mb-2 d-flex justify-content-center" style={{ border: "1px dashed var(--admin-border)" }}>
                  <img src={imagePreview} alt="Preview" style={{ maxHeight: "180px", maxWidth: "100%", objectFit: "contain" }} />
                </div>
                <Button variant="outline-danger" size="sm" onClick={removeImage}>Remove Image</Button>
              </div>
            )}

            <Button type="submit" className="w-100 mt-3 fw-bold py-2 shadow-sm" disabled={uploading} style={{ backgroundColor: "var(--admin-primary)", borderColor: "var(--admin-primary)" }}>
              {uploading ? "Uploading..." : isEdit ? "Update Category" : "Add Category"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showDetailModal} onHide={closeDetailModal} size="lg" centered fullscreen="md-down">
        <Modal.Header closeButton className="border-bottom px-3 px-md-4 py-3 bg-light">
          <Modal.Title className="fw-bold m-0" style={{ fontSize: "1.25rem", color: "var(--admin-text-main)" }}>
            Category Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 p-md-4">
          {selectedCategory && (
            <div>
              <h5 className="mb-3 fw-bold text-dark">{selectedCategory.name}</h5>
              <div className="bg-light rounded mb-4 shadow-sm d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <img src={selectedCategory.image} alt={selectedCategory.name} className="img-fluid rounded" style={{ maxHeight: '400px', width: '100%', objectFit: 'contain' }} />
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
